// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { Dispatch } from 'redux';
import { getJson, postJson } from 'Utils/http';
import type { RootState } from 'Store';
import { buildOrderContext } from 'Features/chatbot/chatOrder';
import { addLayer, setLayerShared, ViewerLayer } from 'Features/EsriSplitViewer/viewerLayersSlice';
import { pinChart, pinWidget, setWidgetShared, PinnedChart } from './dashboardSlice';
import type { WidgetSpec } from './dashboardMetrics';

// Khora's ONE sharing mechanism: the org-shared dashboard set for one geodatabase — pinned CHARTS
// plus shared LAYERS (metadata + khora/ S3 key; the server presigns each layer's url on load) plus
// recomputable WIDGET specs. The server derives org/order from the record and tenancy-gates the call.
export interface SharedDashboardSet {
  pins: PinnedChart[];
  // Shared layers come back with a fresh presigned `url` joined by the server; geojson never travels.
  layers: (Omit<ViewerLayer, 'geojson'> & { url?: string })[];
  // Recomputable widget specs — the recipient's dashboard computes them against its own loaded gdb.
  widgets: WidgetSpec[];
}

// Order-scoped shared-pins endpoint for a gdb (+ auth token), or null when there's no order context.
const sharedPinsTarget = (state: RootState, gdbId: string): { url: string; token?: string } | null => {
  const ctx = buildOrderContext(state);
  if (!ctx) return null;
  return {
    url: `${ctx.base}/orders/${ctx.orgId}/${ctx.projectId}/${ctx.orderId}/gdb-shared-pins/${encodeURIComponent(gdbId)}`,
    token: ctx.token,
  };
};

/**
 * GET the org-shared dashboard set for a gdb and merge it into Redux: an own pin/layer/widget (same id)
 * is flagged shared; a teammate's is added/pinned. Best-effort — a failed fetch leaves the dashboard as
 * is. Staleness-guarded: if a newer gdb loaded while fetching, its own load merges instead of this one.
 */
export const LoadSharedPinsThunk = (gdbId: string) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    const target = sharedPinsTarget(getState(), gdbId);
    if (!target) return;
    const result = await getJson<SharedDashboardSet>(target.url, target.token);
    if (!result.success) return;
    if (getState().dashboard.loadedGdbId !== gdbId) return;

    const { viewerLayers, dashboard } = getState();
    const pins = Array.isArray(result.data.pins) ? result.data.pins : [];
    const layers = Array.isArray(result.data.layers) ? result.data.layers : [];
    const widgets = Array.isArray(result.data.widgets) ? result.data.widgets : [];

    pins.forEach((chart) => dispatch(pinChart({ ...chart, sharedGdbId: gdbId })));
    // Own layer (same id) keeps its richer inline geojson and is just marked shared; a teammate's is
    // added with the server-presigned url.
    layers.forEach((layer) => {
      const own = viewerLayers.layers.find((existing) => existing.id === layer.id);
      if (own) dispatch(setLayerShared({ id: layer.id, sharedGdbId: gdbId }));
      else dispatch(addLayer({ ...layer, sharedGdbId: gdbId, visible: layer.visible !== false }));
    });
    // Own widget is marked shared; a teammate's is pinned (computeSpec recomputes it against this
    // client's loaded gdb on the next refresh).
    widgets.forEach((widget) => {
      const own = dashboard.pinnedWidgets.find((existing) => existing.id === widget.id);
      if (own) dispatch(setWidgetShared({ id: widget.id, sharedGdbId: gdbId }));
      else dispatch(pinWidget({ ...widget, sharedGdbId: gdbId }));
    });
  };

/**
 * POST (replace) the org-shared set for a gdb — last write wins. Layers travel as METADATA + S3 key
 * only (geojson/url stripped here; the server also strips + fence-checks). Returns true on success so
 * the caller can revert its optimistic share toggle when it returns false.
 */
export const SaveSharedPinsThunk = (
  gdbId: string,
  pins: PinnedChart[],
  layers: ViewerLayer[] = [],
  widgets: WidgetSpec[] = [],
) =>
  async (_dispatch: Dispatch, getState: () => RootState): Promise<boolean> => {
    const target = sharedPinsTarget(getState(), gdbId);
    if (!target) return false;
    const layerMeta = layers.filter((layer) => !!layer.key).map(({ geojson, url, ...meta }) => meta);
    const result = await postJson(target.url, { pins, layers: layerMeta, widgets }, target.token);
    return result.success;
  };
